import { defineStore } from 'pinia';
import { StatsService } from '@/services/stats';
import moment from 'moment';
import {
  createCSVDownload,
  getNameWithDateRange,
  createDownloadWithCSV,
} from '@/utils/exportStats';
import { useBotsStore } from '@/stores/bots';
import { useStatsStore } from '@/stores/stats';
import { useIntentsStore } from '@/stores/intents';

/** One CSV export row (date plus dynamic KPI/answer columns) */
type ExportRow = Record<string, any>;

interface ExportStatsState {
  /** Per-day performance KPI rows collected for the CSV export */
  exportsPerformance: ExportRow[];
  /** Per-day per-intent answer rows collected for the CSV export */
  exportsAnswers: ExportRow[];
  /** True while an export is being built (day-by-day fetch loop) */
  isExporting: boolean;
  /** True once the performance export finished */
  isPerformanceDone: boolean;
  /** True once the answers export finished */
  isAnswersDone: boolean;
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/exportStats.ts` (namespace
 * `exportStats`). In-memory only — no `vuexPersist` option (legacy module was never persisted).
 *
 * Collisions & renames (see report at
 * `/private/tmp/claude-501/-Users-haslien-Documents-GitHub-moin-hub/23091b27-f9d9-4b85-91e6-ff6cd1c12710/scratchpad/pinia-migration/reports/exportStats.json`):
 * - Legacy getters `exportsPerformance`, `exportsAnswers`, `isExporting`, `isPerformanceDone`,
 *   `isAnswersDone` were pure identity passthroughs of the state props of the same name. Pinia
 *   merges state and getters onto one instance, so defining both under the same key is a genuine
 *   conflict (unlike Vuex, where `state.x` and `getters.x` live in separate registries). Dropped —
 *   consumers reading `store.exportsPerformance` etc. now hit the state prop directly, which is
 *   the exact same value, so no consumer-facing rename is needed.
 * - Legacy mutations `setValue`, `setExportsPerformance`, `addExportsPerformance`,
 *   `setExportsAnswers`, `addExportsAnswers` become same-named actions (no name collisions with
 *   the existing actions).
 * - Cross-namespace reads (`rootGetters['bots/currentBotId']`, `rootGetters['stats/dateRange']`,
 *   `rootGetters['stats/channel']`, `rootGetters['intents/intents']`) resolve via
 *   `useBotsStore()` / `useStatsStore()` / `useIntentsStore()` called inside the action body.
 */
export const useExportStatsStore = defineStore('exportStats', {
  state: (): ExportStatsState => ({
    exportsPerformance: [],
    exportsAnswers: [],
    isExporting: false,
    isPerformanceDone: false,
    isAnswersDone: false,
  }),
  actions: {
    /**
     * Converted from legacy mutation `setValue`. Generic key/value setter — key is dynamic, so
     * index untyped.
     */
    setValue({ key, value }: { key: string; value: any }) {
      (this as any)[key] = value;
    },
    /** Converted from legacy mutation `setExportsPerformance`. */
    setExportsPerformance({ exports }: { exports: ExportRow[] }) {
      this.exportsPerformance = exports;
    },
    /** Converted from legacy mutation `addExportsPerformance`. */
    addExportsPerformance({ exports }: { exports: ExportRow }) {
      this.exportsPerformance.push(exports);
    },
    /** Converted from legacy mutation `setExportsAnswers`. */
    setExportsAnswers({ exports }: { exports: ExportRow[] }) {
      this.exportsAnswers = exports;
    },
    /** Converted from legacy mutation `addExportsAnswers`. */
    addExportsAnswers({ exports }: { exports: ExportRow }) {
      this.exportsAnswers.push(exports);
    },
    async startPerformanceExport() {
      // legacy read this via rootGetters['bots/currentBotId'] under an untyped `any` context,
      // which tolerated the null-before-first-fetch case the same way this cast does
      const botId = useBotsStore().currentBotId as string;
      const dateRange = useStatsStore().dateRange;
      const channel = useStatsStore().channel;

      this.setValue({ key: 'isExporting', value: true });
      this.setExportsPerformance({ exports: [] });

      // get for each day in dateRange [startDate, endDate] a moment date range
      let dateRanges = [];
      let startDate = dateRange[0];
      let endDate = dateRange[1];
      let days = endDate.diff(startDate, 'days');
      for (let i = 0; i <= days; i++) {
        dateRanges.push(moment(startDate).add(i, 'days'));
      }

      for (let date of dateRanges) {
        const exportConversations: any = await StatsService.getKpi(
          botId,
          'conversations',
          [date, date],
          channel
        );
        const exportAutomation: any = await StatsService.getKpi(
          botId,
          'automation',
          [date, date],
          channel
        );
        const exportFeedbacksResult: any = await StatsService.getFeedbacks(
          botId,
          [date, date],
          channel
        );
        const exportPositiveFeedback = {
          positive: exportFeedbacksResult.find(
            (feedback: any) => feedback.type === 'pos'
          )?.count,
        };
        const exportNegativeFeedback = {
          negative: exportFeedbacksResult.find(
            (feedback: any) => feedback.type === 'neg'
          )?.count,
        };
        let exports: ExportRow = {};
        exports.date = date.format('YYYY-MM-DD');
        exports = {
          ...exports,
          ...exportConversations,
          ...exportAutomation,
          ...exportPositiveFeedback,
          ...exportNegativeFeedback,
        };
        this.addExportsPerformance({ exports });
      }
      this.setValue({ key: 'isExporting', value: false });
      this.setValue({ key: 'isPerformanceDone', value: true });

      return true;
    },
    async startAnswersExport() {
      // legacy read this via rootGetters['bots/currentBotId'] under an untyped `any` context,
      // which tolerated the null-before-first-fetch case the same way this cast does
      const botId = useBotsStore().currentBotId as string;
      const dateRange = useStatsStore().dateRange;
      // legacy read this via rootGetters['intents/intents'] untyped; cast to the array branch it
      // implicitly assumed (the `false` pre-fetch branch was never actually guarded against)
      const intents = useIntentsStore().intents as Array<Record<string, any>>;
      const channel = useStatsStore().channel;
      this.setValue({ key: 'isExporting', value: true });
      this.setExportsAnswers({ exports: [] });

      // get for each day in dateRange [startDate, endDate] a moment date range
      let dateRanges = [];
      let startDate = dateRange[0];
      let endDate = dateRange[1];
      let days = endDate.diff(startDate, 'days');
      for (let i = 0; i <= days; i++) {
        dateRanges.push(moment(startDate).add(i, 'days'));
      }
      // array of intents to json map where key is the intent name and value is the intent id
      let intentsMap: Record<string, string> = {};
      intents.forEach((intent: any) => {
        intentsMap[intent.intent] = intent.displayName;
      });

      // get all intents for each day
      for (let date of dateRanges) {
        let answers: any = await StatsService.getAnswers(botId, [date, date], channel);
        for (let answer of answers.answers) {
          if (!intentsMap[answer.name]) continue;
          let exports: ExportRow = {};
          exports.date = date.format('YYYY-MM-DD');
          exports.intent = intentsMap[answer.name];
          exports.count = answer.count;
          exports.positive = answer.pos;
          exports.negative = answer.neg;
          this.addExportsAnswers({ exports });
        }
      }

      this.setValue({ key: 'isExporting', value: false });
      this.setValue({ key: 'isAnswersDone', value: true });

      return true;
    },
    async downloadCSVPerformance() {
      const csvPerformance = createCSVDownload(this.exportsPerformance);
      const dateRange = useStatsStore().dateRange;
      let documentName = getNameWithDateRange(dateRange);
      documentName += '-performance';
      const anchor = createDownloadWithCSV(csvPerformance, documentName);
      anchor.click();
    },
    async downloadCSVAnswers() {
      const csvAnswers = createCSVDownload(this.exportsAnswers);
      const dateRange = useStatsStore().dateRange;
      let documentName = getNameWithDateRange(dateRange);
      documentName += '-answers';
      const anchor = createDownloadWithCSV(csvAnswers, documentName);
      anchor.click();
    },
    clear() {
      this.setExportsPerformance({ exports: [] });
      this.setExportsAnswers({ exports: [] });
      this.setValue({ key: 'isPerformanceDone', value: false });
      this.setValue({ key: 'isAnswersDone', value: false });
    },
  },
});
